import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Add } from '@mui/icons-material';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import Loadable from '@components/Loadable/Loadable';
import ModalForm from '@components/ModalForm/ModalForm';
import { INewCannedResponsePayload } from '@interfaces/cannedResponses';
import {
  handleCreateNewCannedResponse,
  handleGetCannedResponseCategories,
  handleGetCannedResponsesByCategory,
} from '@store/features/adminInquiries/cannedResponses/actions';
import {
  cannedResponseCategoriesErrorSelector,
  cannedResponseCategoriesIsLoadingSelector,
  cannedResponseCategoriesSelector,
  cannedResponseSelectedCategorySelector,
} from '@store/features/adminInquiries/cannedResponses/selectors';
import {
  removeCannedResponses,
  removeSelectedCategory,
} from '@store/features/adminInquiries/cannedResponses/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import { encodeHtmlText } from '../../SupplementInquiryDetails/SupplementInformationForm/utils';

import CannedResponsesList from './CannedResponsesList/CannedResponsesList';
import NewCannedResponseForm from './NewCannedResponseForm/NewCannedResponseForm';
import { ICannedResponsesModalProps } from './CannedResponsesModal.interface';

import './style.scss';

const formId = 'admin-new-canned-response-form';

const CannedResponsesModal: FC<ICannedResponsesModalProps> = ({
  isMedication = false,
  open,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('cannedResponses');

  const categories = useSelector(cannedResponseCategoriesSelector);
  const categoriesIsLoading = useSelector(cannedResponseCategoriesIsLoadingSelector);
  const categoriesError = useSelector(cannedResponseCategoriesErrorSelector);
  const selectedCategory = useSelector(cannedResponseSelectedCategorySelector);

  const handleFetchCannedResponseCategories = useCallback(() => {
    dispatch(handleGetCannedResponseCategories(isMedication));
  }, [dispatch]);

  useEffect(handleFetchCannedResponseCategories, [handleFetchCannedResponseCategories]);

  const handleSelectCategory = useCallback(
    (event: SelectChangeEvent<number>) => {
      const cannedResponseCategoryId = +event.target.value;
      cannedResponseCategoryId &&
        dispatch(handleGetCannedResponsesByCategory(cannedResponseCategoryId));
    },
    [dispatch],
  );

  const handleApply = useCallback(
    (text: string) => {
      onApply(text);
      onClose();
    },
    [onApply, onClose],
  );

  const handleValidate = useCallback(
    (values: INewCannedResponsePayload) => {
      const errors: Partial<Record<keyof INewCannedResponsePayload, string | boolean>> = {};
      if (!values.comments) {
        errors.comments = true;
      }
      if (!values.cannedResponseCategoryId) {
        errors.cannedResponseCategoryId = true;
      }
      if (
        !isMedication &&
        values.cannedResponseCategoryId === -1 &&
        !values.cannedResponseCategoryName
      ) {
        errors.cannedResponseCategoryName = true;
      }
      return errors;
    },
    [isMedication],
  );

  const handleSubmit = useCallback(
    (values: INewCannedResponsePayload) =>
      dispatch(
        handleCreateNewCannedResponse({
          ...values,
          comments: encodeHtmlText(values.comments),
        }),
      ),
    [dispatch],
  );

  const handleExited = useCallback(() => {
    dispatch(removeCannedResponses());
    dispatch(removeSelectedCategory());
  }, [dispatch]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_CREATE_NEW_CANNED_RESPONSE_SUCCESS, (text: string) => {
      handleToast('success', t('adminInquiries.cannedResponseCreated'));
      handleApply(text);
      handleFetchCannedResponseCategories();
    });

    return () => eventBus.off(EventType.ADMIN_CREATE_NEW_CANNED_RESPONSE_SUCCESS);
  }, [t, eventBus, handleFetchCannedResponseCategories, handleApply, onClose]);

  return (
    <ModalForm<INewCannedResponsePayload>
      onClose={onClose}
      onExited={handleExited}
      open={open}
      formId={formId}
      title={t('adminInquiries.cannedResponses')}
      className="canned-responses-modal"
      leftActionTitle={t('common.cancel')}
      onLeftAction={onClose}
      rightActionTitle={t('adminInquiries.addComment')}
      rightActionIcon={<Add />}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {props => (
        <Loadable
          isLoading={categoriesIsLoading}
          error={categoriesError && t('errors.cannedResponseCategories')}
          onRetry={handleFetchCannedResponseCategories}
        >
          {categories && (
            <div className="canned-responses-modal__body">
              {!!categories.length && (
                <FormControl className="canned-responses-modal__categories" variant="filled">
                  <InputLabel className="form-select__label">
                    {t('adminInquiries.selectCategory')}
                  </InputLabel>

                  <Select
                    className="canned-responses-modal__categories"
                    onChange={handleSelectCategory}
                    value={selectedCategory || undefined}
                  >
                    {categories.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedCategory && (
                <CannedResponsesList categoryId={selectedCategory} onApply={handleApply} />
              )}

              <NewCannedResponseForm
                {...props}
                isMedication={isMedication}
                categories={categories}
                formId={formId}
              />
            </div>
          )}
        </Loadable>
      )}
    </ModalForm>
  );
};

export default CannedResponsesModal;
